import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import {
  DIRECTED_DIRECTOR_RUNS_PER_DAY,
  DIRECTED_DIRECTOR_RUNS_PER_VIDEO,
} from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../../common/types';
import type { DirectedActionError } from '../../directedErrors';
import { useDirectedVideoContext } from '../../DirectedVideoContext';
import { castMessages } from '../../messages/castMessages';
import { directorMessages as messages } from '../../messages/directorMessages';
import type { DirectorWorkKind } from '../DirectorWorking';
import { AlertIcon } from './AlertIcon';
import { boldPart } from '../../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from '../DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

const FAILED: Readonly<Record<DirectorWorkKind, MessageDescriptor>> = {
  stories: messages.failedStories,
  styles: messages.failedStyles,
  script: messages.failedScript,
};

const ERRORS: Readonly<Partial<Record<DirectedActionError, MessageDescriptor>>> = {
  busy: messages.busy,
  stale: messages.stale,
  demo: messages.demo,
  step: messages.failed,
  castPending: castMessages.castNotReady,
  failed: messages.failed,
  consent: messages.failed,
  pictures: messages.failed,
  castFull: messages.failed,
};

interface DirectorNoticesProps {
  kind: DirectorWorkKind;
  /** The refusal of the last write of this step, or null. */
  error: DirectedActionError | null;
  /** "Try again" of a failed run; none: no link. */
  onRetry?: () => void;
  /** False while an earlier step is on view (`?step=`): the failure of the last run is not about it. */
  showRunFailure: boolean;
}

/**
 * The director errors and limits of a step (design c4): a failed run (`directorFailCode`), no runs
 * left for the video, no runs left today, and the refusal of the last write.
 */
const DirectorNoticesComponent: React.FC<DirectorNoticesProps> = ({ kind, error, onRetry, showRunFailure }) => {
  const intl = useIntl();
  const { video } = useDirectedVideoContext();
  const failCode = video.directorState === 'idle' && showRunFailure ? video.directorFailCode : null;
  const noRuns = video.directorRunsLeft <= 0 || error === 'videoLimit';
  const notices: React.ReactNode[] = [];

  if (failCode !== null) {
    notices.push(
      <div key="failed" className={clsx(styles.alert, styles.alertError)} role="alert">
        <AlertIcon kind="warn" />
        <div>
          {intl.formatMessage(failCode === 'AI_BUDGET_EXHAUSTED' ? messages.failedBudget : FAILED[kind], { b: bold })}
          {onRetry !== undefined && !noRuns && (
            <button type="button" className={styles.alertAction} onClick={onRetry}>
              {intl.formatMessage(messages.tryAgain)}
            </button>
          )}
        </div>
      </div>
    );
  }
  if (noRuns) {
    notices.push(
      <div key="video" className={clsx(styles.alert, styles.alertWarn)} role="alert">
        <AlertIcon kind="warn" />
        <div>{intl.formatMessage(messages.videoLimit, { b: bold, count: DIRECTED_DIRECTOR_RUNS_PER_VIDEO })}</div>
      </div>
    );
  }
  if (error === 'dayLimit') {
    notices.push(
      <div key="day" className={clsx(styles.alert, styles.alertWarn)} role="alert">
        <AlertIcon kind="warn" />
        <div>{intl.formatMessage(messages.dayLimit, { b: bold, count: DIRECTED_DIRECTOR_RUNS_PER_DAY })}</div>
      </div>
    );
  }
  const errorText = error === null ? undefined : ERRORS[error];
  if (errorText !== undefined) {
    notices.push(
      <p key="error" className={clsx(styles.alert, styles.alertError)} role="alert">
        <AlertIcon kind="warn" />
        <span>{intl.formatMessage(errorText)}</span>
      </p>
    );
  }
  return notices.length === 0 ? null : <>{notices}</>;
};

export const DirectorNotices = TypedMemo(DirectorNoticesComponent);
